import React from "react";
import LegalLayout from "./LegalLayout";
import { BRAND } from "../../lib/brand";

const E = BRAND.legal.entity;
const APP = BRAND.name;
const EMAIL = BRAND.legal.contactEmail;

const SECTIONS = [
    {
        id: "summary",
        title: "Summary",
        body: (
            <>
                <p>
                    This Privacy Policy explains how {E} (&ldquo;<strong>{APP}</strong>,&rdquo; &ldquo;<strong>we</strong>&rdquo;)
                    collects, uses, and shares personal information when you use our websites, mobile applications, and APIs (the
                    &ldquo;<strong>Service</strong>&rdquo;).
                </p>
                <ul>
                    <li>We collect account info, content you post, and basic device/usage data.</li>
                    <li>We <strong>do not sell</strong> your personal information.</li>
                    <li>You can access, correct, export, or delete your data at any time.</li>
                    <li>EU/UK, California, and other state-law residents have additional rights described below.</li>
                </ul>
            </>
        ),
    },
    {
        id: "controller",
        title: "Who is the data controller?",
        body: (
            <>
                <p>
                    {E} is the data controller of personal information processed in connection with the Service.
                </p>
                <p>
                    For privacy questions or to exercise your rights, contact{" "}
                    <a href={`mailto:${EMAIL}`}>{EMAIL}</a> or write to {BRAND.legal.mailingAddress}.
                </p>
            </>
        ),
    },
    {
        id: "what-we-collect",
        title: "What we collect",
        body: (
            <>
                <h3>You provide directly</h3>
                <ul>
                    <li><strong>Account</strong> &mdash; email, password (hashed), name, username, optional avatar, home region, interests.</li>
                    <li><strong>Content</strong> &mdash; adventures, photos, videos, GPS routes, comments, marketplace listings, messages.</li>
                    <li><strong>Billing</strong> &mdash; payment-method details collected and tokenised by our payment processor (we never see card numbers).</li>
                    <li><strong>Communications</strong> &mdash; emails or messages you send to support.</li>
                </ul>
                <h3>Collected automatically</h3>
                <ul>
                    <li><strong>Device</strong> &mdash; IP address, user-agent, OS, browser, app version, language, time-zone.</li>
                    <li><strong>Usage</strong> &mdash; pages viewed, actions taken, error logs, performance metrics.</li>
                    <li><strong>Cookies and similar technologies</strong> &mdash; see <em>Cookies</em> below.</li>
                    <li><strong>Approximate location</strong> &mdash; derived from IP. Precise location only with your explicit permission (e.g., when you geotag an adventure).</li>
                </ul>
                <h3>From third parties</h3>
                <ul>
                    <li>Authentication providers (e.g., Google) when you log in via OAuth.</li>
                    <li>Payment processors (e.g., Helcim) for transaction status.</li>
                    <li>Push-notification providers (e.g., Expo) for delivery receipts.</li>
                </ul>
                <p>We do <strong>not</strong> intentionally collect sensitive categories of data such as health, biometric, or government-ID information.</p>
            </>
        ),
    },
    {
        id: "how-we-use",
        title: "How we use your information",
        body: (
            <>
                <p>We use personal information to:</p>
                <ul>
                    <li>Provide, secure, and improve the Service (operate the feed, store adventures, deliver messages).</li>
                    <li>Authenticate users and prevent fraud, abuse, or unauthorized access.</li>
                    <li>Process subscriptions and marketplace transactions.</li>
                    <li>Personalise your feed and recommendations.</li>
                    <li>Send transactional emails (password reset, billing, security alerts).</li>
                    <li>Send product updates, digest emails, and marketing &mdash; only where permitted (you can opt out anytime).</li>
                    <li>Respond to your support requests.</li>
                    <li>Comply with legal obligations and enforce our Terms.</li>
                </ul>
            </>
        ),
    },
    {
        id: "legal-basis",
        title: "Legal bases (EU/UK/EEA users)",
        body: (
            <>
                <p>If you are in the EEA, UK, or Switzerland, we rely on these GDPR Article 6 bases:</p>
                <ul>
                    <li><strong>Contract</strong> &mdash; to provide the Service you signed up for (Art. 6(1)(b)).</li>
                    <li><strong>Legitimate interests</strong> &mdash; to keep the Service safe, prevent abuse, conduct analytics, and communicate transactionally (Art. 6(1)(f)).</li>
                    <li><strong>Consent</strong> &mdash; for non-essential cookies, marketing emails, and precise location (Art. 6(1)(a)). You may withdraw consent at any time without affecting prior processing.</li>
                    <li><strong>Legal obligation</strong> &mdash; for tax, accounting, and law-enforcement requests (Art. 6(1)(c)).</li>
                </ul>
            </>
        ),
    },
    {
        id: "sharing",
        title: "How we share information",
        body: (
            <>
                <p>We share personal information only as follows:</p>
                <ul>
                    <li><strong>Other users</strong> &mdash; content you post is visible to others according to your privacy settings (e.g., public adventures, friends-only posts).</li>
                    <li><strong>Service providers (processors)</strong> &mdash; cloud hosting, database, payments (Helcim), email (Resend), object storage, analytics, push notifications. They process data only on our instructions.</li>
                    <li><strong>Legal &amp; safety</strong> &mdash; if required by law, valid legal process, or to protect rights, property, or safety.</li>
                    <li><strong>Business transfers</strong> &mdash; if we merge with or are acquired by another company, your data may transfer subject to this Policy.</li>
                </ul>
                <p>
                    <strong>We do not sell</strong> personal information for money. We do not engage in cross-context behavioural
                    advertising or share personal information for advertising purposes outside our own first-party Service.
                </p>
            </>
        ),
    },
    {
        id: "international",
        title: "International data transfers",
        body: (
            <>
                <p>
                    {APP} is operated from the United States. If you access the Service from outside the U.S., your data is
                    transferred to and processed in the U.S. and other countries where we or our processors operate. For
                    transfers from the EEA, UK, or Switzerland to the U.S., we rely on the European Commission&rsquo;s{" "}
                    <em>Standard Contractual Clauses</em> and equivalent safeguards. You may request a copy at{" "}
                    <a href={`mailto:${EMAIL}`}>{EMAIL}</a>.
                </p>
            </>
        ),
    },
    {
        id: "retention",
        title: "How long we keep data",
        body: (
            <>
                <ul>
                    <li><strong>Account data</strong> &mdash; while your account is active. After deletion, we remove within 30 days, except where retention is required by law (e.g., tax, fraud prevention).</li>
                    <li><strong>Content you post</strong> &mdash; until you delete it; backup copies are purged within 90 days.</li>
                    <li><strong>Logs &amp; security data</strong> &mdash; up to 13 months.</li>
                    <li><strong>Billing records</strong> &mdash; up to 7 years (tax law).</li>
                </ul>
            </>
        ),
    },
    {
        id: "security",
        title: "Security",
        body: (
            <>
                <p>
                    We use industry-standard administrative, technical, and physical safeguards including encryption in transit
                    (TLS 1.2+), encryption at rest for databases, password hashing (bcrypt), least-privilege access controls, and
                    audit logging. No method of transmission or storage is 100% secure; please use a strong, unique password and
                    enable any available account-security features.
                </p>
            </>
        ),
    },
    {
        id: "cookies",
        title: "Cookies and similar technologies",
        body: (
            <>
                <p>We use:</p>
                <ul>
                    <li><strong>Strictly necessary</strong> cookies for login (httpOnly session cookie) and CSRF protection. These cannot be disabled.</li>
                    <li><strong>Functional</strong> cookies for preferences (e.g., theme, dismissed banners).</li>
                    <li><strong>Analytics</strong> cookies to understand aggregate usage (set only with consent in regions that require it).</li>
                </ul>
                <p>You can manage cookies via your browser settings. Disabling strictly-necessary cookies will prevent login.</p>
            </>
        ),
    },
    {
        id: "rights-eu",
        title: "Your rights — EU / UK / EEA (GDPR)",
        body: (
            <>
                <p>
                    If GDPR applies to you, you have the right to:
                </p>
                <ul>
                    <li><strong>Access</strong> a copy of the personal data we hold about you.</li>
                    <li><strong>Rectify</strong> inaccurate or incomplete data.</li>
                    <li><strong>Erase</strong> your data (&ldquo;right to be forgotten&rdquo;).</li>
                    <li><strong>Restrict</strong> or <strong>object to</strong> processing based on legitimate interests.</li>
                    <li><strong>Portability</strong> &mdash; receive your data in a machine-readable format.</li>
                    <li><strong>Withdraw consent</strong> at any time, where processing is based on consent.</li>
                    <li><strong>Lodge a complaint</strong> with your local Data Protection Authority. A list is available at{" "}
                        <a href="https://edpb.europa.eu/about-edpb/about-edpb/members_en" target="_blank" rel="noreferrer">edpb.europa.eu</a>.
                    </li>
                </ul>
                <p>
                    To exercise any of these rights, email <a href={`mailto:${EMAIL}`}>{EMAIL}</a>. We respond within 30 days
                    (extendable by 60 days for complex requests, with notice).
                </p>
            </>
        ),
    },
    {
        id: "rights-us",
        title: "Your rights — California, Colorado, Connecticut, Virginia, Utah, and other U.S. states",
        body: (
            <>
                <p>
                    Residents of California (CCPA/CPRA), Colorado (CPA), Connecticut (CTDPA), Virginia (VCDPA), Utah (UCPA), and
                    other states with comprehensive privacy laws have the right to:
                </p>
                <ul>
                    <li><strong>Know</strong> what personal information we collect, use, disclose, and (if applicable) sell or share.</li>
                    <li><strong>Access</strong> a copy of your personal information.</li>
                    <li><strong>Delete</strong> your personal information, subject to legal exceptions.</li>
                    <li><strong>Correct</strong> inaccurate personal information.</li>
                    <li><strong>Opt out of &ldquo;sales&rdquo; and &ldquo;sharing&rdquo;</strong> &mdash; we do not engage in either, but the right is preserved.</li>
                    <li><strong>Limit use of sensitive personal information</strong>.</li>
                    <li><strong>Non-discrimination</strong> &mdash; we will not deny service, charge different prices, or provide a different level of quality because you exercised a right.</li>
                </ul>
                <p>
                    Submit a request from <em>Settings → Privacy</em> or by emailing{" "}
                    <a href={`mailto:${EMAIL}`}>{EMAIL}</a>. You may use an authorised agent. We verify identity using account
                    credentials or, for non-account-holders, two pieces of personal information that match our records. Verified
                    requests are answered within 45 days (extendable by 45 days, with notice).
                </p>
                <p>
                    California residents may also request the categories of personal information disclosed for a business purpose
                    in the past 12 months &mdash; see <em>How we share information</em> above for the canonical list.
                </p>
                <p>
                    <strong>&ldquo;Do Not Sell or Share My Personal Information&rdquo;:</strong> we do not sell or share personal
                    information for cross-context behavioural advertising. No action is required, but you may confirm by
                    emailing <a href={`mailto:${EMAIL}`}>{EMAIL}</a>.
                </p>
            </>
        ),
    },
    {
        id: "children",
        title: "Children",
        body: (
            <>
                <p>
                    The Service is intended for adults <strong>18 and older</strong>. We do not knowingly collect personal
                    information from anyone under 18 (including children under 13 within the meaning of the U.S. Children&rsquo;s
                    Online Privacy Protection Act, &ldquo;COPPA&rdquo;).
                </p>
                <p>
                    If you believe a minor has provided personal information through the Service, contact us at{" "}
                    <a href={`mailto:${EMAIL}`}>{EMAIL}</a> and we will promptly delete it.
                </p>
            </>
        ),
    },
    {
        id: "automated",
        title: "Automated decision-making and profiling",
        body: (
            <>
                <p>
                    We use light personalisation (e.g., recommended adventures based on your stated interests). These features do
                    not produce legal or similarly significant effects on you. We do not engage in profiling that has legal
                    effects within the meaning of GDPR Article 22.
                </p>
            </>
        ),
    },
    {
        id: "third-party-links",
        title: "Third-party services and links",
        body: (
            <>
                <p>
                    The Service may include links to third-party websites or services not controlled by {E}. Their privacy
                    practices are governed by their own policies; we are not responsible for them.
                </p>
            </>
        ),
    },
    {
        id: "changes",
        title: "Changes to this Policy",
        body: (
            <>
                <p>
                    We may update this Policy from time to time. Material changes will be communicated by email or in-app notice
                    at least 14 days before they take effect. The &ldquo;Last updated&rdquo; date at the top reflects the latest
                    revision.
                </p>
            </>
        ),
    },
    {
        id: "contact",
        title: "Contact us",
        body: (
            <>
                <p>
                    For privacy questions or to exercise any rights described above:
                </p>
                <p>
                    <strong>{E}</strong><br />
                    {BRAND.legal.mailingAddress}<br />
                    Email: <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
                </p>
            </>
        ),
    },
];

export default function Privacy() {
    return (
        <LegalLayout
            testid="privacy-page"
            title="Privacy Policy"
            lede={`Your privacy matters. This Policy explains what ${APP} collects, why, and the rights you have under GDPR (EU/UK), CCPA/CPRA (California), and other state and international privacy laws.`}
            sections={SECTIONS}
        />
    );
}
